import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import styled from 'styled-components';

import { ControleOpenstaandClient, ReviewClient, ReviewedRegelDto, ReviewedRegelsDto, StatusEnum, ControleOpenstaand, KleurStatusEnum }
  from '../client/procescontroleclient';
import Table from "../components/tables/Table";
import CheckBox from '../components/UI/CheckBox';
import Button from "../components/UI/Button";
import Refresh from "../components/UI/Refresh";
import PageTitle from '../components/UI/PageTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import themeColors from '../config/themeColors';
import { useMediaQuery } from 'react-responsive';
import ButtonWrap, { MobileButtonWrap } from '../components/UI/ButtonWrap';
import Input from '../components/UI/Input';
import ProcesControleMobileCard, { Row, Item, Label, Field } from "../components/UI/ProcesControleMobileCard";
import { ButtonType } from "../Types";
import Spinner from "../components/UI/Spinner";
import { getDateTimeFormat } from "../utilities/Utilities";
import { InputType, TableType } from '../Types';
import useDebounce from "../hooks/useDebounce";
import PageWrapper from '../components/UI/PageWrapper';

interface Props {
  color: KleurStatusEnum;
}

const Tr = styled.tr<Props>`
  ${props => props.color===KleurStatusEnum.Red ? 'background-color: rgba(235,100,96,0.5)!important;' : null}; 
`;

export default function ProcesControleOpenstaand() {
  const [selectedRows, setSelectedRows] = useState(Array<number | undefined>);
  const [filter, setFilter] = useState<string>('');
  const openstaandClient = new ControleOpenstaandClient(process.env.REACT_APP_APIURL);
  const opgelostClient = new ReviewClient(process.env.REACT_APP_APIURL);
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const debouncedFilter = useDebounce(filter, 300);

  const checkFilterRow: any = (filterRow: ControleOpenstaand) => {
    if (debouncedFilter === '') return filterRow;
    const filterableKeys = ['omschrijving', 'debiteurnummer', 'banduitgang', 'afdeling', 'hub'];
    return filterableKeys.some((key) => (filterRow[key as keyof ControleOpenstaand] as string).toLowerCase().includes(debouncedFilter.toLowerCase()));
  }

  const openstaandQuery = useQuery(['controleOpenstaand', 'all'], async () => {
    return await openstaandClient.get();
  }, {
    staleTime: Infinity
  });

  const filteredOpenstaandData = useMemo(() => {

    if (openstaandQuery.data) {
      return openstaandQuery.data.filter(checkFilterRow);
    }
    return [];
  }, [openstaandQuery.data, debouncedFilter]);

  const createSolvedRowsMutation = useMutation(async (reviewedRows: ReviewedRegelsDto) => {
    return await opgelostClient.create(reviewedRows);
  }, {
    onSuccess: () => {
      openstaandQuery.refetch();
      setSelectedRows([]);
    }
  });

  const checkBoxHandler = (barCode: number | undefined) => {
    if (selectedRows.includes(barCode)) {
      setSelectedRows(selectedRows.filter((item) => item !== barCode));
    } else {
      setSelectedRows([...selectedRows, barCode]);
    }
  }

  const opgelostHandler = () => {
    const reviewedDto = new ReviewedRegelsDto();
    reviewedDto.status = StatusEnum.Opgelost;

    reviewedDto.reviewedOrderProcesControleRows = selectedRows.map((barcode) => {
      const reviewedRow = new ReviewedRegelDto();
      var row = openstaandQuery.data?.find((item) => item.barcode === barcode);
      if (row !== undefined) {
        reviewedRow.barcode = row.barcode;
        reviewedRow.sourceCode = row.sourceCode;
        reviewedRow.orderStatus = row.orderStatus;
      }
      return reviewedRow;
    });

    createSolvedRowsMutation.mutate(reviewedDto);

    return;
  }


  if (openstaandQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <PageWrapper>
      <PageTitle>Openstaande regels</PageTitle>
      <ButtonWrap>
        {!isMobile ? <Button buttonType={ButtonType.Primary} spacingR svg onClick={() => openstaandQuery.refetch()}><FontAwesomeIcon icon="eye" />Opvragen</Button>
          : <Refresh onClick={() => openstaandQuery.refetch()} />}
        <MobileButtonWrap>
          <Input
            inputType={InputType.SearchInput}
            onChange={(e: React.FormEvent<HTMLInputElement>) => setFilter(e.currentTarget.value)}
            value={filter}
          />
          <Button disabled={createSolvedRowsMutation.isLoading || selectedRows.length==0} buttonType={ButtonType.Primary} onClick={() => opgelostHandler()} 
                  indicator={selectedRows.length.toString()} color={themeColors.ftgPastelGreen}>Opgelost</Button>
        </MobileButtonWrap>
      </ButtonWrap>
      {!isMobile ?
        <Table tableType={TableType.Default} stickymodal>
          <thead>
            <tr>
              <th>Banduitgang</th>
              <th>Debnr</th>
              <th>Hub</th>
              <th>Afdeling</th>
              <th>Omschrijving</th>
              <th style={{ textAlign: 'right' }}>Kolli</th>
              <th style={{ textAlign: 'right' }}>Inh kolli</th>
              <th style={{ textAlign: 'right' }}>Los</th>
              <th style={{ textAlign: 'right' }}>Stuks</th>
              <th style={{ textAlign: 'right' }}>Lengte</th>
              <th>Picktijd</th>
              <th>Inpaktijd</th>
              <th style={{ width: '2%' }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredOpenstaandData?.map((row, index) => {
              return (
                <Tr key={index} color={row.businessRuleAfgegaan===true ? KleurStatusEnum.Red : KleurStatusEnum.Empty}>
                  <td>{row.banduitgang}</td>
                  <td>{row.debiteurnummer}</td>
                  <td>{row.hub}</td>
                  <td>{row.afdeling}</td>
                  <td>{row.omschrijving}</td>
                  <td style={{ textAlign: 'right' }}>{row.kolli}</td>
                  <td style={{ textAlign: 'right' }}>{row.kolliInhoud}</td>
                  <td style={{ textAlign: 'right' }}>{row.los}</td>
                  <td style={{ textAlign: 'right' }}>{row.stuks}</td>
                  <td style={{ textAlign: 'right' }}>{row.lengte}</td>
                  <td>{getDateTimeFormat(row.picktijd)}</td>
                  <td>{getDateTimeFormat(row.inpaktijd)}</td>
                  <td>
                    <CheckBox
                      checked={selectedRows.includes(row.barcode)}
                      onClick={() => checkBoxHandler(row.barcode)}
                    />
                  </td>
                </Tr>
              )
            })}
          </tbody>
        </Table> :
        filteredOpenstaandData?.map((row, index) => (
          <ProcesControleMobileCard color={row.businessRuleAfgegaan ? KleurStatusEnum.Red : KleurStatusEnum.Empty}
            compactView={
              <>
                <div><strong>Omschr.</strong>{row.omschrijving.substring(0, 15)}{row.omschrijving.length >= 15 && '..'}</div>
                <div><strong>Debnr.</strong>{row.debiteurnummer}</div>
              </>
            }
            expandView={
              <>
                <Row>
                  <Item><Label>Omschrijving</Label><Field>{row.omschrijving}</Field></Item>
                </Row>
                <Row>
                  <Item>
                    <Label>Debnr</Label><Field>{row.debiteurnummer}</Field>
                  </Item>
                  <Item>
                    <Label>Hub</Label><Field>{row.hub}</Field>
                  </Item>
                </Row>
                <Row>
                  <Item>
                    <Label>Afdeling</Label><Field>{row.afdeling}</Field>
                  </Item>
                  <Item>
                    <Label>Lengte</Label><Field>{row.lengte}</Field>
                  </Item>
                </Row>
                <Row>
                  <Item>
                    <Label>Kolli</Label><Field>{row.kolli}</Field>
                  </Item>
                  <Item>
                    <Label>Kolli inh.</Label><Field>{row.kolliInhoud}</Field>
                  </Item>
                  <Item>
                    <Label>Los</Label><Field>{row.los}</Field>
                  </Item>
                  <Item>
                    <Label>Stuks</Label><Field>{row.stuks}</Field>
                  </Item>
                </Row>
                <Row>
                  <Item>
                    <Label>Picktijd</Label><Field>{getDateTimeFormat(row.picktijd)}</Field>
                  </Item>
                  <Item>
                    <Label>Inpaktijd</Label><Field>{getDateTimeFormat(row.inpaktijd)}</Field>
                  </Item>
                </Row>
              </>
            }
            checked={selectedRows.includes(row.barcode!)}
            onclick={() => checkBoxHandler(row.barcode!)}
            title={row.banduitgang} />
        ))}
    </PageWrapper>
  );
}